import { ApolloClient, makeVar, split } from '@apollo/client';
import { InMemoryCache, TypePolicies } from '@apollo/client/cache';
import { HttpLink } from '@apollo/client/link/http';
import ApolloLinkTimeout from 'apollo-link-timeout';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from 'apollo-utilities';
import { createClient } from 'graphql-ws';
import WebsocketConstants from '../constants/WebsocketConstants';
import {
  Zrm_ConversationResponse,
  Zrm_GetLeadManagementLeadsResponse,
  Zrm_GetPropertySearchResultResponse
} from '../types/GraphqlTypes';
import { ModalType } from '../types/ModalType';

const GRAPHQL_ENDPOINT = '/rental-manager/inbox/graphql';
const TIMEOUT_IN_SECONDS = 15;

/**
 * used to manually tell other components that ConversationList is fetching data
 */
export const inboxPageIsLoadingVariable = makeVar(false);

/**
 * Have a component generate the display name that other components can used without needing to repeat logic or prop drill
 */
export const inboxPageDisplayNameVariable = makeVar('');

/**
 * Used to "transport" text for AddMessage component to use to append to the input text
 * AddMessage will listen for this content and reset it to '' once it's consumed
 */
export const inboxPageAddMessageInputTextToAppendVariable = makeVar('');

/**
 * Used to "transport" text to SavedReplyModal component to use to create a new saved reply
 */
export const inboxPageCreateSavedReplyTextVariable = makeVar('');

/**
 *  Focus the add message input box
 */
export const inboxPageIsAddMessageInputFocusedVariable = makeVar(false);

/**
 *  Clear the message input box and then append the text to it
 */
export const inboxPageClearAndAppendMessageInputTextVariable = makeVar('');

/**
 *  For scrolling to the renter profile attributes section
 */
export const inboxPageShouldScrollToRenterProfileVariable = makeVar(false);

/**
 *  For MessageList to use to know when to NOT mark conversation as read. Can be used by other components
 */
export const inboxPagePreventMarkAsReadVariable = makeVar(false);

/**
 *  For MessageList/MessageListItems to know when to NOT scroll to the bottom of the list.
 *  Used by inbox and can be used by lead management (e.g. RenterMessagingModal) or other consumers.
 */
export const preventMessageListScrollVariable = makeVar(false);

/**
 *  For ConversationList to use to know when to filter conversations by a specific conversationId
 */
export const inboxPageConversationIdToFilterByVariable = makeVar('');

/**
 *  For ConversationList to use to clear the search text from the empty container view
 */
export const inboxPageConversationSearchTextVariable = makeVar('');

/**
 *  For ConversationList, MessageList and RenterProfile components to use to know when to display an empty container
 */
export const inboxPageShowEmptyContainerVariable = makeVar(false);

/**
 *  For switching focus between ConversationSearchBar and AddMessage components
 */
export const inboxPageIsConversationSearchBarFocusedVariable = makeVar(false);

/**
 *  For consistency across components on lead management page when requesting a profile
 */
export const leadManagementShowProfileRequestedForConversationIdVariable = makeVar('');

/**
 *  For Lead Management's RenterDetailsModal component to use to know when and which section to scroll to upon opening
 */
export const leadManagementRenterDetailsSectionToScrollToVariable = makeVar<
  'latestContact' | 'notepad' | 'renterDetailsHeader' | 'renterProfile' | ''
>('');

/**
 * Used in the middlewares to handle auth redirect
 */
export const serverSideRedirectVariable = makeVar<string | null>(null);

/**
 * Modal state management in the form of a reactive variable.
 * Pass in null to turn off any active modals
 * Pass in a ModalType enum to turn on a modal
 */
export const activeModalVariable = makeVar<ModalType | null>(null);

/**
 *  For MessageList to use to know when to refetch messages on websocket push
 */
export const shouldRefetchMessagesVariable = makeVar(false);

const headers: Record<string, string | undefined> = {
  'build_number': process.env.NEXT_PUBLIC_BUILD_NUMBER,
  'x-client-app-name': 'satellite-landlord-web',
};

const timeoutLink = new ApolloLinkTimeout(1000 * TIMEOUT_IN_SECONDS);

const httpLink = new HttpLink({
  uri: __DEV__ ? `${process.env.NEXT_PUBLIC_ZG_API}${GRAPHQL_ENDPOINT}` : GRAPHQL_ENDPOINT, // relative url for prod and qa
  credentials: 'include',
  fetch,
  headers,
});

const timeoutHttpLink = timeoutLink.concat(httpLink);

const typePolicies: TypePolicies = {
  ZRM_Conversation: {
    keyFields: ['conversationId'],
    fields: {
      applicationAlreadySent: {
        keyArgs: false,
        merge(existing?: boolean | null | undefined, incoming?: boolean | null | undefined) {
          // getAllForConversation returns applicationAlreadySent
          // getLatestConversations does not.
          // Without a merge function to deal with this descrepency,
          // ConversationList calls will overwrite existing values for this field
          return typeof existing === 'boolean' ? existing : incoming;
        },
      },
      applicationCompleted: {
        keyArgs: false,
        merge(existing?: boolean | null | undefined, incoming?: boolean | null | undefined) {
          return typeof existing === 'boolean' ? existing : incoming;
        },
      },
      isApplicationsAccepted: {
        keyArgs: false,
        merge(existing?: boolean | null | undefined, incoming?: boolean | null | undefined) {
          return !!existing || !!incoming;
        },
      },
      conversation: {
        keyArgs: false,
        /**
         * Merge scenarios
         * 1) ConversationList poll finds a new message (incoming comes second)
         * 2) MessageList loads messages on initial fetch (incoming comes first)
         * 3) MessageList poll finds a new message (incoming comes second)
         * 4) MessageList pagination (incoming comes first)
         */
        merge(existing: any, incoming: any, { variables }) {
          if (existing) {
            if (variables?.conversationId) {
              if (variables?.paginationStartIndex) {
                return mergeMessages([...incoming, ...existing]);
              }
            }

            // If a single incoming message already exists don't merge it with existing messages
            // to prevent out of order messages
            if (incoming.length === 1) {
              const existingMessageRefs = existing.flatMap((existingItem: any) => existingItem['__ref']);

              if (existingMessageRefs.includes(incoming[0]['__ref'])) {
                return [...existing];
              }
            }

            return mergeMessages([...existing, ...incoming]);
          }

          return [...incoming];
        },
      },
    },
  },
  ZRM_Message: {
    keyFields: ['conversationId', 'messageId'],
    fields: {
      message: {
        keyArgs: false,
        merge(existing?: string | null | undefined, incoming?: string | null | undefined) {
          return incoming || existing || '';
        },
      },
      messagePreview: {
        keyArgs: false,
        merge(existing?: string | null | undefined, incoming?: string | null | undefined) {
          return incoming || existing || '';
        },
      },
    },
  },
  ZRM_Note: {
    keyFields: ['conversationId'],
  },
  ZRM_Attachment: {
    keyFields: ['zDocId'],
    fields: {
      previewUrl: {
        keyArgs: false,
        merge(existing?: string | null | undefined, incoming?: string | null | undefined) {
          return existing || incoming;
        },
      },
    },
  },
  SavedReply: {
    keyFields: ['replyIndex', 'reply', 'title'],
  },
  ZRM_PropertyListing: {
    keyFields: ['listingAlias'],
  },
  ZRM_LeadManagementLead: {
    keyFields: ['conversationId'],
  },
  Query: {
    fields: {
      ZRM_getPropertySearchResult: {
        keyArgs: ['searchText'],
        merge(existing: Zrm_GetPropertySearchResultResponse, incoming: Zrm_GetPropertySearchResultResponse) {
          if (!existing) {
            return incoming;
          }

          return {
            ...incoming,
            listings: [...existing.listings, ...incoming.listings],
          };
        },
      },
      ZRM_getLeadManagementLeads: {
        keyArgs: [
          'conversationId',
          'folder',
          'listingAlias',
          'minCriteriaMatches',
          'readRepliedStatus',
          'renterProgressStatus',
        ],
        merge(existing: Zrm_GetLeadManagementLeadsResponse, incoming: Zrm_GetLeadManagementLeadsResponse) {
          if (!existing) {
            return incoming;
          }

          const uniqueIncomingLeads = incoming.leads.filter((lead: any) => !existing.leads.some((existingLead: any) => existingLead['__ref'] === lead['__ref']));

          return {
            ...incoming,
            leads: [...existing.leads, ...uniqueIncomingLeads],
          };
        },
      },
      // Allows queries to ZRM_getConversationById to first search through the cache
      // In case the data queried already was fetched by ZRM_getLatestConversations, etc.
      ZRM_getConversationById: {
        keyArgs: ['conversationId'],
        read(_, { args, toReference }) {
          return toReference({
            __typename: 'ZRM_Conversation',
            conversationId: args?.conversationId,
          });
        },
      },
      ZRM_getLatestConversationsV2: {
        keyArgs: [
          'conversationId',
          'folder',
          'readRepliedStatus',
          'renterProgressStatus',
          'listingAlias',
          'startDateYyyyMmDd',
        ],
        merge(existing: Zrm_ConversationResponse, incoming: Zrm_ConversationResponse, { args, variables }) {
          // Only handle merging of conversations
          if (!incoming.conversations) {
            return incoming;
          }

          let newConversations;
          const isPagination = !!args?.paginationStartIndex || !!args?.startIndex;
          let responseObj;

          if (variables?.skipMerge || !existing) {
            responseObj = {
              ...incoming,
              conversations: [...incoming.conversations],
            };
          } else {
            const merged = isPagination
              ? [...existing.conversations, ...incoming.conversations]
              : [...incoming.conversations, ...existing.conversations];

            const rest =
              isPagination || (variables?.daysBack && !variables?.paginationStartIndex) ? incoming : existing;

            newConversations = ((arr: any, key: any) => {
              return [...new Map(arr.map((item: any) => [item[key], item])).values()];
            })(merged, '__ref');

            responseObj = {
              ...rest,
              conversations: newConversations,
            };
          }

          return responseObj;
        },
      },
      // Local Variables
      serverSideRedirectUrl: {
        read() {
          return serverSideRedirectVariable();
        },
      },
      inboxPageIsLoading: {
        read() {
          return inboxPageIsLoadingVariable();
        },
      },
      activeModal: {
        read() {
          return activeModalVariable();
        },
      },
      inboxPageDisplayName: {
        read() {
          return inboxPageDisplayNameVariable();
        },
      },
      inboxPageAddMessageInputTextToAppend: {
        read() {
          return inboxPageAddMessageInputTextToAppendVariable();
        },
      },
      inboxPageCreateSavedReplyText: {
        read() {
          return inboxPageCreateSavedReplyTextVariable();
        },
      },
      inboxPageIsAddMessageInputFocused: {
        read() {
          return inboxPageIsAddMessageInputFocusedVariable();
        },
      },
      inboxPageClearAndAppendMessageInputText: {
        read() {
          return inboxPageClearAndAppendMessageInputTextVariable();
        },
      },
      inboxPageShouldScrollToRenterProfile: {
        read() {
          return inboxPageShouldScrollToRenterProfileVariable();
        },
      },
      inboxPagePreventMarkAsRead: {
        read() {
          return inboxPagePreventMarkAsReadVariable();
        },
      },
      preventMessageListScroll: {
        read() {
          return preventMessageListScrollVariable();
        },
      },
      inboxPageConversationIdToFilterBy: {
        read() {
          return inboxPageConversationIdToFilterByVariable();
        },
      },
      inboxPageShowEmptyContainer: {
        read() {
          return inboxPageShowEmptyContainerVariable();
        },
      },
      inboxPageConversationSearchText: {
        read() {
          return inboxPageConversationSearchTextVariable();
        },
      },
      inboxPageIsConversationSearchBarFocused: {
        read() {
          return inboxPageIsConversationSearchBarFocusedVariable();
        },
      },
      leadManagementShowProfileRequestedForConversationId: {
        read() {
          return leadManagementShowProfileRequestedForConversationIdVariable();
        },
      },
      leadManagementRenterDetailsSectionToScrollTo: {
        read() {
          return leadManagementRenterDetailsSectionToScrollToVariable();
        },
      },
    },
  },
};

let wsLink: GraphQLWsLink | null = null;

  if ((process as any).browser) {
    class MyWebSocket extends WebSocket {
      constructor(url: string) {
        super(url);
      }

      send(payload: string) {
        super.send('apollo-ws|' + payload);
      }
    }

    const client = createClient({
      url: WebsocketConstants.RENTAL_MANAGER_WS_URL,
      webSocketImpl: MyWebSocket,
    });

    wsLink = new GraphQLWsLink(client);
  }

  const link =
    (process as any).browser && wsLink != null
      ? split(
          ({ query }) => {
            const definition = getMainDefinition(query);
            return definition.kind === 'OperationDefinition' && definition.operation === 'subscription';
          },
          wsLink,
          timeoutHttpLink,
        )
      : timeoutHttpLink;

const apolloClient = new ApolloClient({
  link: link,
  cache: new InMemoryCache({
    typePolicies,
  }),
});

/**
 *
 * @param arr of apollo normalized message references, possibly containing duplicates
 * @returns an array of deduped messages
 * @description when merging a conversation (an array of messages), we want to merge from the bottom
 * so that recent messages persist at the bottom. This function dedupes from the end of the array vs the front
 */
function mergeMessages(arr: any) {
  const newArr: Array<any> = [];
  const keySet: Set<string> = new Set();

  for (let i = arr.length - 1; i >= 0; i--) {
    const newKey = arr[i]['__ref'];
    if (!keySet.has(newKey)) {
      newArr.unshift(arr[i]);
      keySet.add(newKey);
    }
  }
  return newArr;
}


export default apolloClient;
